import { ReactNode, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "./style.module.scss";
import PrintPage, { EXPORT_PDF_QUERY_PARAM } from "@components/print/PrintElements/PrintPage";
import PrintPageHeader from "@components/print/PrintElements/PrintPageHeader";
import PrintPageFooter from "@components/print/PrintElements/PrintPageFooter";
import { useAppSelector } from "@/store/hooks";
import PrintDocumentHeader from "@components/print/PrintElements/PrintDocumentHeader";
import DocumentInfo from "@components/print/PrintElements/DocumentInfo";
import ProductList from "@components/print/PrintElements/ProductList";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { readDocumentOtherChargesTotal } from "@utils/DocumentOtherCharges";
import { readDocumentOtherChargesLabel } from "@utils/ClientShipping";
import { formatDate } from "@utils/Api";
import { CustomerInfoBase } from "@/formTypes/DocumentClientType";
import { chunkArray } from "@utils/Utils";
import { sortProductsByOrder } from "@utils/EDocument";
import { checkInvoiceTicketPrint } from "@utils/ticketPrintUtils";
import { EINVOICE_EXPORT_INVOICE_TYPE_CODE } from "@utils/Constants";
import { calculateTotalIVADevuelto, toEdocumentPaymentWays } from "@utils/IvaDevuelto";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { ShippingGuidePages } from "@components/print/PrintElements/ShippingGuide";
import { SHIPPING_GUIDE_QUERY_PARAM } from "@utils/ShippingGuide";

const InvoicePrint = () => {
  const params = useParams()
  const { id: invoiceId, autoPrint = false, copies = 0 } = params;

  const suscription = useAppSelector((state) => { return state.suscription.suscription });
  const maxItemPerPage = Number(suscription?.items_per_page || 30)
  const [invoice, setInvoice] = useState<InvoiceDataType>({})
  const [invoiceProdCount, setInvoiceProdCount] = useState<number>(0)
  const [invoicesProducts, setInvoicesProducts] = useState<EDocumentProductDataType[]>()
  const [documentPages, setDocumentPages] = useState<EDocumentProductDataType[][]>()
  const [searchParams] = useSearchParams();
  const exportFlag = searchParams.get(EXPORT_PDF_QUERY_PARAM);
  /**
   * La guia solo acompana a la factura cuando se imprime recien emitida: la ruta se abre con
   * `?guide=1` solo en ese momento. El render del PDF que viaja en el correo de facturacion
   * electronica pasa por `?export=1`, y ahi la guia nunca debe salir.
   */
  const showShippingGuide = searchParams.get(SHIPPING_GUIDE_QUERY_PARAM) === "1"
    && !exportFlag
    && Number(invoice.bultos || 0) > 0;

  useEffect(() => {
    import("@printerVariation/generic.scss");
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadInvoiceData()
  }, []);

  useEffect(() => {
    const pages = chunkArray(invoicesProducts || [], maxItemPerPage)
    setDocumentPages(pages)
  }, [maxItemPerPage, invoicesProducts]);

  useEffect(() => {
    if (!exportFlag && autoPrint && suscription?.id && invoice?.id) {
      checkInvoiceTicketPrint(suscription, invoice)
    }
  }, [suscription?.id, invoice?.id]);

  const loadInvoiceData = async () => {
    if (invoiceId) {
      const invoice = await InvoiceApi.get(invoiceId, {
        selectionSet: [
          ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "invoiceProducts.*",
          "invoiceProducts.product.*",
          "invoicePayments.*"
        ]
      })
      setInvoicesProducts(sortProductsByOrder(invoice.invoiceProducts))
      setInvoiceProdCount(invoice.invoiceProducts?.length || 0)
      setInvoice(invoice)
    }
    PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
  }

  function toCustomerInfoBase(p: InvoiceDataType): CustomerInfoBase {
    if (!p?.nombre_factura) throw new Error("nombre_factura requerido");
    return {
      nombre_factura: p.nombre_factura,
      razon_social: p.razon_social ?? "",
      tel1: p.tel1 ?? "",
      tel2: p.tel2 ?? "",
      email: p.email ?? "",
      direccion: p.direccion ?? "",
      clientActivitycode: p.clientActivitycode ?? "",
      plazo: p.plazo ?? null,
      client: p.client ?? null,
      tipo_identificacion: p.tipo_identificacion ?? "",
      identificacion: p.identificacion ?? "",
    };
  }

  /**
   * IVA Devuelto de la factura (servicios de salud pagados con tarjeta). Se calcula
   * al imprimir a partir de las lineas y pagos persistidos, igual que en el payload
   * enviado a Hacienda. En la factura de exportacion el campo es inexistente.
   */
  const getInvoiceIVADevuelto = (): number => {
    if (invoice.einvoice_kind == EINVOICE_EXPORT_INVOICE_TYPE_CODE) {
      return 0;
    }
    const products = (invoice.invoiceProducts || []) as EDocumentProductDataType[];
    const ventaNeta = products.reduce(
      (total, p) => total + Number(p.subtotal || 0) - Number(p.descuento || 0),
      0
    );
    return calculateTotalIVADevuelto(
      products,
      toEdocumentPaymentWays(invoice.invoicePayments || []),
      ventaNeta
    );
  }


  /** Total de "Otros Cargos" (nodo OtrosCargos v4.4) del documento persistido. */
  const getInvoiceOtrosCargos = (): number => readDocumentOtherChargesTotal(invoice);

  /** Rotulo de la fila de cargos: se renombra si el documento solo cobra el envio. */
  const getInvoiceOtrosCargosLabel = (): string => readDocumentOtherChargesLabel(invoice);

  const renderPage = (documentPages: EDocumentProductDataType[][]): ReactNode[] => {
    return documentPages.map((page, index) => {
      return (
        <PrintPage
          nula={invoice.anulada || false}
          autoPrint={autoPrint === "1"}
          copies={Number(copies)}
        >
          <PrintPageHeader suscription={suscription} />
          <div className="page-content">
            <PrintDocumentHeader title={`Factura ${invoice.einvoice_kind == EINVOICE_EXPORT_INVOICE_TYPE_CODE ? "Exportacion" : ""} #${invoice.numero}`} fecha={formatDate(invoice.fecha, "YYYY-MM-DD", "DD.MM.YY")} />
            <DocumentInfo
              customer={toCustomerInfoBase(invoice)}
              docType="Factura"
              docSequenceData={{
                docKey: invoice.invoice_key || "",
                sequence: invoice.sequence || ""
              }}
            />
            <ProductList
              maxItemPerPage={maxItemPerPage}
              products={page}
              productCount={invoiceProdCount}
            />
            <PrintPageFooter
              currencyCode={invoice.currency.currency_id}
              exchangeRate={invoice.exchange_rate || 0}
              comments={[
                `${suscription?.invoice_info}`,
                `${invoice.document_comments}`
              ]}
              summary={{
                subtotal: invoice.subtotal || 0,
                descuento: invoice.descuento || 0,
                impuestos: invoice.impuestos || 0,
                total: invoice.total || 0,
                ivaDevuelto: getInvoiceIVADevuelto(),
                otrosCargos: getInvoiceOtrosCargos(),
                otrosCargosLabel: getInvoiceOtrosCargosLabel()
              }}
              currentPage={(index + 1)}
              totalPages={documentPages.length} />
          </div>

        </PrintPage>
      )
    });
  }

  return (
    <div>
      {invoice?.id && documentPages && renderPage(documentPages)}
      {invoice?.id && showShippingGuide && (
        <ShippingGuidePages invoice={invoice} suscription={suscription} />
      )}
      {!invoice.id && (
        <h3> Cargando </h3>
      )}

    </div>
  );
}

export default InvoicePrint;
